import { useTranslation } from 'react-i18next';
import styled, { createGlobalStyle } from 'styled-components';
import { type OptionSettingDetailsFragment } from '../../../../../api/fragments/OptionSettingDetails';
import { ComboType } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import SelectField from '../../../../../components/fields/SelectField';
import useTranslatedString from '../../../../../utilities/useTranslatedString';

const ComboFieldContainer = styled.div`
    & label {
        font-size: 18px;
        height: 37px;
        border-bottom: 1px solid #d9d9d9;

        &:after {
            display: none;
        }
    }

    & .ant-form-item-explain {
        text-align: right;
    }

    & > .ant-form-item > .ant-form-item-row {
        flex-wrap: initial;

        & > .ant-form-item-label {
            flex: initial;
        }

        & > .ant-form-item-control {
            flex: 1 1 0;
        }
    }
`;

const StyledSelectField = styled(SelectField)`
    &.ant-select {
        &:not(.ant-select-customize-input) {
            & .ant-select-selection-item,
            .ant-select-selection-placeholder {
                padding-top: 3px;
                font-size: 18px;
                text-align: right;
            }

            &:hover > .ant-select-selector {
                border-color: #d9d9d9;
            }

            &.ant-select-focused > .ant-select-selector {
                border-color: #d9d9d9;
            }

            & > .ant-select-selector {
                height: 37px;
                padding-right: 0;
            }
        }

        & > .ant-select-arrow {
            right: 0;
        }
    }
`;

const StyledInputField = styled(InputField)`
    &.ant-input,
    input.ant-input {
        font-size: 18px;
        text-align: right;
        padding-right: 0;

        &:hover {
            border-color: #d9d9d9;
        }

        &:active {
            border-color: #d9d9d9;
        }

        &.ant-input:focus,
        .ant-input-focused {
            border-color: #d9d9d9;
        }
    }
`;

const SelectDropdownGlobalStyle = createGlobalStyle`
    .option-combo-dropdown-popup {
        text-align: right;
        width: auto !important;

        & .ant-select-item-option-content {
            font-size: 16px;
        }
    }
`;

export type OptionComboFieldProps = {
    name: string;
    optionSetting: OptionSettingDetailsFragment;
    openWidgetDrawer: boolean;
};

const OptionComboField = ({ name, optionSetting, openWidgetDrawer }: OptionComboFieldProps) => {
    const { t } = useTranslation('configuratorDetails');
    const translatedString = useTranslatedString();

    if (optionSetting.__typename !== 'ComboOptionSettings') {
        return null;
    }

    return (
        <ComboFieldContainer>
            {!openWidgetDrawer && <SelectDropdownGlobalStyle />}
            {optionSetting.options.map((option, index) => {
                switch (option.comboType) {
                    case ComboType.Dropdown:
                        return (
                            <StyledSelectField
                                key={option.id}
                                label={translatedString(option.label)}
                                name={`${name}[${index}].value`}
                                options={option.descriptions.map(description => ({
                                    label: translatedString(description),
                                    value: translatedString(description),
                                }))}
                                popupClassName="option-combo-dropdown-popup"
                            />
                        );

                    case ComboType.TextField:
                        return (
                            <StyledInputField
                                key={option.id}
                                itemProps={
                                    option.charLimit && {
                                        help: t('configuratorDetails:messages.maximumCharacters', {
                                            count: option.charLimit,
                                        }),
                                    }
                                }
                                label={translatedString(option.label)}
                                maxLength={option.charLimit}
                                name={`${name}[${index}].value`}
                                placeholder={option.placeholder}
                            />
                        );

                    default:
                        throw new Error('Invalid combo type');
                }
            })}
        </ComboFieldContainer>
    );
};

export default OptionComboField;
